import React, { useLayoutEffect, useRef } from 'react'

import { TableCellProps, Cell as GridCell } from 'react-table'
import styled from 'styled-components'

import { Tooltip } from '_components/tooltip'
import { palette } from '_styles/variables'

const CellTd = styled.td<{ showColumnDividers?: boolean }>`
  font-size: 0.875rem;
  padding-left: 8px;
  padding-right: 0.5rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  line-height: 1.25rem;
  display: inline-flex;
  border-left: ${({ showColumnDividers }) => (showColumnDividers ? 1 : 0)}px
    solid ${palette.neutral['grey-d8d8d8']};
  border-bottom: ${({ showColumnDividers }) => (showColumnDividers ? 1 : 0)}px
    solid ${palette.neutral['grey-d8d8d8']};

  &:last-child {
    padding-right: 1.25rem;
    border-right: ${({ showColumnDividers }) => (showColumnDividers ? 1 : 0)}px
      solid ${palette.neutral['grey-d8d8d8']};
  }
`

const TdDiv = styled.div`
  display: flex;
  align-items: center;
`

const EllipsisDiv = styled.div<{ maxLines?: number }>`
  display: -webkit-box;
  -webkit-line-clamp: ${({ maxLines }) => maxLines};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

type CellValues = string | number | boolean

export interface CellProps<D extends object, V extends CellValues> {
  cell: GridCell<D, V>
  cellProps: TableCellProps & { title?: string }
  defaultMaxLines?: number | undefined
  defaultAddTooltip?: boolean
  showColumnDividers?: boolean
  title?: string
}

export const Cell = <D extends object, V extends CellValues>({
  cell,
  cellProps,
  defaultMaxLines = 2,
  defaultAddTooltip = false,
  showColumnDividers = false,
}: CellProps<D, V>) => {
  const ref = useRef<HTMLTableCellElement>(null)

  useLayoutEffect(() => {
    if (ref && ref.current) {
      const { clientWidth, scrollWidth } = ref.current

      ref.current.title = (
        clientWidth > scrollWidth ? '' : cell.value
      ) as string
    }
  }, [cell.value])

  const addTooltip = cell.column.addTooltip ?? defaultAddTooltip
  const tooltipLabel = cell.value && cell.value.toString()

  return (
    <CellTd {...cellProps} ref={ref} showColumnDividers={showColumnDividers}>
      <TdDiv>
        <EllipsisDiv
          maxLines={
            cell.column?.maxLines ? cell.column.maxLines : defaultMaxLines
          }
        >
          {addTooltip ? (
            <Tooltip label={tooltipLabel} shouldShow>
              <span>{cell.render('Cell')}</span>
            </Tooltip>
          ) : (
            cell.render('Cell')
          )}
        </EllipsisDiv>
      </TdDiv>
    </CellTd>
  )
}
